import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { robotoRegular } from "./fonts/roboto-regular";
import { robotoBold } from "./fonts/roboto-bold";
import { UNIT_CODES } from "./romanianCounties";
import { COMPANY_INFO } from "./companyInfo";

interface InvoiceData {
  invoice_number: string | null;
  issue_date: string;
  due_date: string | null;
  seller_name: string | null;
  seller_cui: string | null;
  seller_address: string | null;
  seller_city?: string | null;
  seller_county?: string | null;
  buyer_name: string | null;
  buyer_cui: string | null;
  buyer_address: string | null;
  buyer_city?: string | null;
  buyer_county?: string | null;
  subtotal: number | null;
  vat_total: number | null;
  total: number | null;
  items: {
    item_name: string;
    quantity: number;
    unit_price: number;
    vat_rate: number;
    unit_code?: string;
  }[];
}

const formatRON = (v: number) =>
  v.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function generateInvoicePdf(data: InvoiceData) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  // Register Roboto font with Romanian diacritics support
  doc.addFileToVFS("Roboto-Regular.ttf", robotoRegular);
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
  doc.addFileToVFS("Roboto-Bold.ttf", robotoBold);
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  let y = 15;

  // ── Disclaimer banner ──
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, y, pageW - margin * 2, 10, "F");
  doc.setDrawColor(180);
  doc.rect(margin, y, pageW - margin * 2, 10, "S");
  doc.setFontSize(8);
  doc.setFont("Roboto", "bold");
  doc.setTextColor(180, 0, 0);
  doc.text("COPIE DE LUCRU — Documentul fiscal oficial se află în sistemul SPV al ANAF", pageW / 2, y + 6.5, { align: "center" });
  doc.setTextColor(0);
  y += 16;

  // ── Title ──
  doc.setFontSize(14);
  doc.setFont("Roboto", "bold");
  doc.text("FACTURA", margin, y);
  y += 7;

  // ── Invoice meta ──
  doc.setFontSize(9);
  doc.setFont("Roboto", "normal");
  const nr = data.invoice_number || "—";
  doc.text(`Nr.: ${nr}`, margin, y);
  y += 5;
  doc.text(`Data emiterii: ${data.issue_date}`, margin, y);
  if (data.due_date) {
    y += 5;
    doc.text(`Data scadenței: ${data.due_date}`, margin, y);
  }
  y += 8;

  // ── Seller / Buyer table ──
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);

  const halfW = (pageW - margin * 2) / 2;

  // Header row
  doc.setFillColor(230, 230, 230);
  doc.rect(margin, y, halfW, 7, "FD");
  doc.rect(margin + halfW, y, halfW, 7, "FD");
  doc.setFontSize(8);
  doc.setFont("Roboto", "bold");
  doc.text("FURNIZOR", margin + 2, y + 5);
  doc.text("CLIENT", margin + halfW + 2, y + 5);
  y += 7;

  // Content rows
  const sellerLocation = [data.seller_city, data.seller_county].filter(Boolean).join(", ");
  const buyerLocation = [data.buyer_city, data.buyer_county].filter(Boolean).join(", ");

  const sellerLines = [
    data.seller_name || "—",
    data.seller_cui ? `CUI: ${data.seller_cui}` : "",
    data.seller_address || "",
    sellerLocation,
  ].filter(Boolean);

  const buyerLines = [
    data.buyer_name || "—",
    data.buyer_cui ? `CUI: ${data.buyer_cui}` : "",
    data.buyer_address || "",
    buyerLocation,
  ].filter(Boolean);

  const maxLines = Math.max(sellerLines.length, buyerLines.length);
  const cellH = maxLines * 5 + 4;

  doc.rect(margin, y, halfW, cellH, "S");
  doc.rect(margin + halfW, y, halfW, cellH, "S");

  doc.setFontSize(9);
  doc.setFont("Roboto", "normal");

  sellerLines.forEach((line, i) => {
    if (i === 0) doc.setFont("Roboto", "bold");
    else doc.setFont("Roboto", "normal");
    doc.text(line, margin + 2, y + 4 + i * 5);
  });

  buyerLines.forEach((line, i) => {
    if (i === 0) doc.setFont("Roboto", "bold");
    else doc.setFont("Roboto", "normal");
    doc.text(line, margin + halfW + 2, y + 4 + i * 5);
  });

  y += cellH + 6;

  // ── Items Table ──
  const getUnitLabel = (code?: string) => {
    if (!code) return "BUC";
    const found = UNIT_CODES.find(u => u.code === code);
    return found ? found.name.split(" (")[0] : code;
  };

  const tableBody = data.items.map((item, idx) => [
    (idx + 1).toString(),
    item.item_name,
    formatRON(item.quantity),
    getUnitLabel(item.unit_code),
    formatRON(item.unit_price),
    formatRON(item.quantity * item.unit_price),
    `${item.vat_rate}%`,
    formatRON(item.quantity * item.unit_price * (item.vat_rate / 100)),
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Nr.", "Denumire produs / serviciu", "Cantitate", "UM", "Preț unitar", "Valoare", "Cotă TVA", "Valoare TVA"]],
    body: tableBody,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      font: "Roboto",
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [230, 230, 230],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      2: { halign: "right", cellWidth: 18 },
      3: { halign: "center", cellWidth: 14 },
      4: { halign: "right", cellWidth: 22 },
      5: { halign: "right", cellWidth: 22 },
      6: { halign: "center", cellWidth: 18 },
      7: { halign: "right", cellWidth: 22 },
    },
  });

  y = (doc as any).lastAutoTable.finalY + 8;

  // ── Totals ──
  const totalsStartX = pageW - margin - 80;
  const totalsW = 80;
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);

  // Subtotal row
  doc.setFontSize(9);
  doc.setFont("Roboto", "normal");
  doc.rect(totalsStartX, y, totalsW / 2, 7, "S");
  doc.rect(totalsStartX + totalsW / 2, y, totalsW / 2, 7, "S");
  doc.text("Subtotal:", totalsStartX + 2, y + 5);
  doc.text(`${formatRON(data.subtotal ?? 0)} RON`, totalsStartX + totalsW - 2, y + 5, { align: "right" });
  y += 7;

  // TVA row
  doc.rect(totalsStartX, y, totalsW / 2, 7, "S");
  doc.rect(totalsStartX + totalsW / 2, y, totalsW / 2, 7, "S");
  doc.text("TVA:", totalsStartX + 2, y + 5);
  doc.text(`${formatRON(data.vat_total ?? 0)} RON`, totalsStartX + totalsW - 2, y + 5, { align: "right" });
  y += 7;

  // Total row
  doc.setFont("Roboto", "bold");
  doc.setFillColor(230, 230, 230);
  doc.rect(totalsStartX, y, totalsW / 2, 8, "FD");
  doc.rect(totalsStartX + totalsW / 2, y, totalsW / 2, 8, "FD");
  doc.setFontSize(10);
  doc.text("TOTAL:", totalsStartX + 2, y + 5.5);
  doc.text(`${formatRON(data.total ?? 0)} RON`, totalsStartX + totalsW - 2, y + 5.5, { align: "right" });

  // ── Footer ──
  const footerY = pageH - 10;
  doc.setFontSize(7);
  doc.setFont("Roboto", "normal");
  doc.setTextColor(150);
  doc.text(
    `Generat cu ${COMPANY_INFO.brandName} — copie de lucru, documentul oficial este cel din SPV (ANAF)`,
    pageW / 2,
    footerY,
    { align: "center" }
  );

  return doc;
}
